import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type {
  ServiceLineTreeFilterProps,
  TreeRowsProps,
} from "./serviceLineTreeTypes";
import {
  getDisplayLabelForSelectedUnit,
  isCanonicalServiceLineAllNode,
} from "./serviceLineTreeUtils";
import {
  buildBranchOpenForFilteredSections,
  buildExpansionStateForSelection,
  buildNodeExpandedDefaultsForSearch,
  filterServiceLineRootsForSearch,
} from "./serviceLineTreeSearchUtils";
import {
  serviceLineTreeFilterLayout,
  serviceLineTreeFilterStyles,
  serviceLineTreeFilterTextLeafNested,
  serviceLineTreeFilterTextPrimary,
} from "./serviceLineTreeFilterStyles";
import { SelectedValueTooltip } from "../../../FilterDropdowns";

const TreeRows: React.FC<TreeRowsProps> = ({
  nodes,
  depth,
  expanded,
  toggle,
  onSelectValue,
  selectedServiceLine,
  treeKeyPrefix,
}) => {
  const theme = useTheme();
  const rowInsetPx =
    serviceLineTreeFilterLayout.treeBaseIndentPx +
    depth * serviceLineTreeFilterLayout.treeDepthStepPx;
  const leafColor = (isLeaf: boolean) =>
    isLeaf && depth >= 1
      ? serviceLineTreeFilterTextLeafNested
      : serviceLineTreeFilterTextPrimary;

  return (
    <Box sx={serviceLineTreeFilterStyles.treeColumn}>
      {nodes.map((node, nodeIndex) => {
        const children = node.children ?? [];
        const rowKey = `${treeKeyPrefix}${depth}-${nodeIndex}-${node.name}`;
        const hasChildren = children.length > 0;
        const isOpen = expanded[rowKey] ?? false;
        const showDividerUnderLabel = !hasChildren || !isOpen;
        const isAllLeaf = isCanonicalServiceLineAllNode(node) && !hasChildren;
        const isNullFolder =
          hasChildren && node.space_organization_unit_id === null;
        const isSelected =
          (isAllLeaf && selectedServiceLine === "") ||
          (node.space_organization_unit_id !== null &&
            String(node.space_organization_unit_id) === selectedServiceLine);

        const handleLabelClick = () => {
          if (isNullFolder && !isCanonicalServiceLineAllNode(node)) {
            return;
          }
          if (isCanonicalServiceLineAllNode(node) && !hasChildren) {
            onSelectValue("");
            return;
          }
          if (!hasChildren && node.space_organization_unit_id === null) {
            onSelectValue("");
            return;
          }
          if (node.space_organization_unit_id !== null) {
            onSelectValue(String(node.space_organization_unit_id));
          }
        };

        return (
          <Box
            key={rowKey}
            sx={serviceLineTreeFilterStyles.treeNodeWrapper(
              hasChildren,
              isOpen,
            )}
          >
            <Box
              sx={serviceLineTreeFilterStyles.treeRow(
                theme,
                rowInsetPx,
                showDividerUnderLabel,
                isSelected,
              )}
            >
              {hasChildren ? (
                <Box sx={serviceLineTreeFilterStyles.treeChevronCell}>
                  <IconButton
                    size="small"
                    aria-expanded={isOpen}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(rowKey);
                    }}
                    sx={serviceLineTreeFilterStyles.iconButton}
                  >
                    {isOpen ? (
                      <ExpandMoreIcon
                        sx={serviceLineTreeFilterStyles.chevronIcon}
                      />
                    ) : (
                      <ChevronRightIcon
                        sx={serviceLineTreeFilterStyles.chevronIcon}
                      />
                    )}
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ pl: "2px", flexShrink: 0 }} aria-hidden />
              )}
              <Typography
                variant="body2"
                onClick={handleLabelClick}
                sx={{
                  ...serviceLineTreeFilterStyles.treeRowLabel,
                  ...(hasChildren
                    ? serviceLineTreeFilterStyles.branchRowTypography
                    : serviceLineTreeFilterStyles.treeLeafTypography),
                  color: leafColor(!hasChildren),
                }}
              >
                {node.name}
              </Typography>
            </Box>
            {hasChildren && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <TreeRows
                  nodes={children}
                  depth={depth + 1}
                  expanded={expanded}
                  toggle={toggle}
                  onSelectValue={onSelectValue}
                  selectedServiceLine={selectedServiceLine}
                  treeKeyPrefix={treeKeyPrefix}
                />
              </Collapse>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const UnitHierarchyFilter: React.FC<ServiceLineTreeFilterProps> = ({
  tree,
  serviceLine,
  onChange,
  disabled = false,
  loading = false,
  filtersApplyEpoch = 0,
  fieldLabel = "Service Line",
  emptySearchMessage = "No matching service lines",
  inputName = "service-line-filter",
  panelId = "service-line-tree-panel",
  clearSelectionAriaLabel = "Clear service line",
  defaultExpandedRootSectionName = "service lines",
}) => {
  const theme = useTheme();
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const fieldInputRef = React.useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverWidthPx, setPopoverWidthPx] = useState<number | null>(null);
  const [branchOpen, setBranchOpen] = useState<Record<string, boolean>>({});
  const [nodeExpanded, setNodeExpanded] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [emptyAfterClear, setEmptyAfterClear] = useState(false);

  const hasTree = Array.isArray(tree) && tree.length > 0;

  useEffect(() => {
    setEmptyAfterClear(false);
  }, [filtersApplyEpoch]);

  useEffect(() => {
    if (serviceLine !== "") {
      setEmptyAfterClear(false);
    }
  }, [serviceLine]);

  const displayRoots = useMemo(() => {
    if (!hasTree || !tree) {
      return [];
    }
    return filterServiceLineRootsForSearch(tree, searchQuery);
  }, [tree, hasTree, searchQuery]);

  useEffect(() => {
    if (!hasTree || !tree) {
      setAnchorEl(null);
      setPopoverWidthPx(null);
      setBranchOpen({});
      setNodeExpanded({});
      return;
    }
    const q = searchQuery.trim();
    const filteredRoots = q
      ? filterServiceLineRootsForSearch(tree, searchQuery)
      : tree;

    if (!q) {
      const selectedId = emptyAfterClear ? "" : serviceLine;
      const { branchOpen: nextBranchOpen, nodeExpanded: nextNodeExpanded } =
        buildExpansionStateForSelection(
          filteredRoots,
          selectedId,
          defaultExpandedRootSectionName,
        );
      setBranchOpen(nextBranchOpen);
      setNodeExpanded(nextNodeExpanded);
      return;
    }

    setBranchOpen(
      buildBranchOpenForFilteredSections(
        filteredRoots,
        searchQuery,
        defaultExpandedRootSectionName,
      ),
    );
    const merged: Record<string, boolean> = {};
    filteredRoots.forEach((section, sectionIndex) => {
      const sectionChildren = section.children ?? [];
      if (sectionChildren.length === 0) {
        return;
      }
      Object.assign(
        merged,
        buildNodeExpandedDefaultsForSearch(
          sectionChildren,
          0,
          searchQuery,
          `${sectionIndex}-`,
        ),
      );
    });
    setNodeExpanded(merged);
  }, [
    tree,
    hasTree,
    searchQuery,
    defaultExpandedRootSectionName,
    serviceLine,
    emptyAfterClear,
  ]);

  const toggleSection = (sectionKey: string) => {
    setBranchOpen((p) => ({ ...p, [sectionKey]: !(p[sectionKey] ?? true) }));
  };

  const toggleNode = (k: string) =>
    setNodeExpanded((p) => ({ ...p, [k]: !p[k] }));

  const display = useMemo(() => {
    if (emptyAfterClear && serviceLine === "") {
      return "";
    }
    if (!hasTree) {
      return "";
    }
    return getDisplayLabelForSelectedUnit(tree, serviceLine) ?? "";
  }, [tree, serviceLine, hasTree, emptyAfterClear]);

  const fieldDisabled = disabled || !hasTree;

  const resetTreeExpansionToDefaults = useCallback(() => {
    if (!hasTree || !tree) {
      setBranchOpen({});
      setNodeExpanded({});
      return;
    }
    const selectedId = emptyAfterClear ? "" : serviceLine;
    const { branchOpen: nextBranchOpen, nodeExpanded: nextNodeExpanded } =
      buildExpansionStateForSelection(
        tree,
        selectedId,
        defaultExpandedRootSectionName,
      );
    setBranchOpen(nextBranchOpen);
    setNodeExpanded(nextNodeExpanded);
  }, [
    hasTree,
    tree,
    defaultExpandedRootSectionName,
    serviceLine,
    emptyAfterClear,
  ]);

  const ensurePopoverOpen = useCallback(() => {
    if (fieldDisabled || !anchorRef.current) {
      return;
    }
    if (!anchorEl) {
      setSearchQuery("");
      resetTreeExpansionToDefaults();
      setPopoverWidthPx(anchorRef.current.offsetWidth);
      setAnchorEl(anchorRef.current);
    }
  }, [fieldDisabled, anchorEl, resetTreeExpansionToDefaults]);

  const close = () => {
    setAnchorEl(null);
    setSearchQuery("");
    setPopoverWidthPx(null);
    resetTreeExpansionToDefaults();
  };

  const handleSelectValue = useCallback(
    (value: string) => {
      setEmptyAfterClear(false);
      onChange(value);
      close();
    },
    [onChange],
  );

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) {
      return;
    }
    const id = globalThis.setTimeout(() => {
      const input = fieldInputRef.current;
      input?.focus({ preventScroll: true });
    }, 0);
    return () => globalThis.clearTimeout(id);
  }, [open]);

  const showSelectionClear =
    !open && !fieldDisabled && Boolean(display?.trim());
  const showSearchClear = open && Boolean(searchQuery.trim());
  const showAnyClear = showSelectionClear || showSearchClear;

  const handleClearField = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEmptyAfterClear(true);
    onChange("");
    globalThis.setTimeout(() => {
      fieldInputRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  const handleClearSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchQuery("");
  };

  const closedPlaceholder =
    hasTree && tree ? `${tree.length} Options` : "0 Options";
  const tooltipTitle = !open && display ? display : "";

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontSize: "12px",
          fontWeight: 400,
          color: theme.palette.text.primary,
          mb: "0px",
        }}
      >
        {fieldLabel}
      </Typography>
      <Box
        ref={anchorRef}
        sx={{
          ...serviceLineTreeFilterStyles.fieldWrapper,
          ...(showAnyClear
            ? serviceLineTreeFilterStyles.fieldClearRevealOnHover
            : {}),
        }}
      >
        {open ? (
          <TextField
            inputRef={fieldInputRef}
            size="small"
            fullWidth
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={() => ensurePopoverOpen()}
            onFocus={() => ensurePopoverOpen()}
            disabled={fieldDisabled}
            autoComplete="off"
            name={inputName}
            aria-expanded={open}
            aria-controls={panelId}
            aria-haspopup="listbox"
            slotProps={{
              htmlInput: {
                readOnly: false,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={serviceLineTreeFilterStyles.fieldEndAdornmentRow}
                >
                  {showSearchClear && (
                    <IconButton
                      type="button"
                      size="small"
                      className="service-line-clear-indicator"
                      aria-label="Clear search"
                      onClick={handleClearSearch}
                      sx={serviceLineTreeFilterStyles.fieldClearIconButton}
                    >
                      <CloseIcon
                        sx={serviceLineTreeFilterStyles.fieldClearIcon}
                      />
                    </IconButton>
                  )}
                  <ExpandMoreIcon
                    sx={{
                      ...serviceLineTreeFilterStyles.fieldExpandIcon,
                      transform: "rotate(180deg)",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              ...serviceLineTreeFilterStyles.textField(fieldDisabled),
              "& .MuiInputBase-input": {
                fontSize: "14px",
                cursor: "text",
                "&:hover": {
                  cursor: "text",
                },
              },
            }}
          />
        ) : (
          <Box
            tabIndex={fieldDisabled ? -1 : 0}
            aria-expanded={false}
            aria-haspopup="listbox"
            aria-controls={undefined}
            onClick={() => ensurePopoverOpen()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                ensurePopoverOpen();
              }
            }}
            sx={serviceLineTreeFilterStyles.closedFieldBox(fieldDisabled)}
          >
            <Box sx={serviceLineTreeFilterStyles.closedFieldValueSlot}>
              {tooltipTitle ? (
                <SelectedValueTooltip title={tooltipTitle}>
                  <Box
                    component="span"
                    sx={serviceLineTreeFilterStyles.closedFieldValue}
                  >
                    {display}
                  </Box>
                </SelectedValueTooltip>
              ) : (
                <Box
                  component="span"
                  sx={serviceLineTreeFilterStyles.closedFieldValue}
                >
                  {closedPlaceholder}
                </Box>
              )}
            </Box>
            <Box sx={serviceLineTreeFilterStyles.fieldEndAdornmentRow}>
              {showSelectionClear && (
                <IconButton
                  type="button"
                  size="small"
                  className="service-line-clear-indicator"
                  aria-label={clearSelectionAriaLabel}
                  onClick={handleClearField}
                  sx={serviceLineTreeFilterStyles.fieldClearIconButton}
                >
                  <CloseIcon sx={serviceLineTreeFilterStyles.fieldClearIcon} />
                </IconButton>
              )}
              <ExpandMoreIcon
                sx={serviceLineTreeFilterStyles.fieldExpandIcon}
              />
            </Box>
          </Box>
        )}
        {loading && (
          <CircularProgress
            size={20}
            sx={serviceLineTreeFilterStyles.loadingProgress(showAnyClear)}
          />
        )}
      </Box>

      {hasTree && tree ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          marginThreshold={8}
          disableAutoFocus
          disableRestoreFocus
          disableEnforceFocus
          slotProps={{
            paper: {
              elevation: 0,
              id: panelId,
              sx: [
                serviceLineTreeFilterStyles.popoverPaper,
                ...(popoverWidthPx == null
                  ? []
                  : [
                      {
                        width: popoverWidthPx,
                        minWidth: popoverWidthPx,
                        maxWidth: popoverWidthPx,
                        boxSizing: "border-box" as const,
                      },
                    ]),
              ],
            },
          }}
        >
          <Box sx={serviceLineTreeFilterStyles.popoverContent}>
            {displayRoots.length === 0 ? (
              <Typography sx={serviceLineTreeFilterStyles.popoverEmptySearch}>
                {emptySearchMessage}
              </Typography>
            ) : (
              <Box sx={serviceLineTreeFilterStyles.grayPanel}>
                {displayRoots.map((section, index) => {
                  const sectionKey = `section-${index}`;
                  const sectionChildren = section.children ?? [];
                  const hasSectionChildren = sectionChildren.length > 0;
                  const isAllLeaf =
                    isCanonicalServiceLineAllNode(section) &&
                    !hasSectionChildren;

                  if (isAllLeaf) {
                    return (
                      <Box
                        key={sectionKey}
                        onClick={() => handleSelectValue("")}
                        sx={serviceLineTreeFilterStyles.allRowBox(
                          theme,
                          serviceLine === "",
                        )}
                      >
                        <Typography
                          variant="body2"
                          sx={serviceLineTreeFilterStyles.allRowTypography}
                        >
                          {section.name}
                        </Typography>
                      </Box>
                    );
                  }

                  const sectionExpanded = branchOpen[sectionKey] ?? false;
                  const showSectionHeaderPointer =
                    section.space_organization_unit_id !== null;

                  const isSectionSelected =
                    section.space_organization_unit_id !== null &&
                    String(section.space_organization_unit_id) === serviceLine;

                  return (
                    <React.Fragment key={sectionKey}>
                      <Box
                        sx={serviceLineTreeFilterStyles.branchSectionRow(
                          theme,
                          sectionExpanded,
                          isSectionSelected,
                        )}
                      >
                        {hasSectionChildren && (
                          <IconButton
                            size="small"
                            onClick={() => toggleSection(sectionKey)}
                            sx={serviceLineTreeFilterStyles.iconButton}
                            aria-expanded={sectionExpanded}
                          >
                            {sectionExpanded ? (
                              <ExpandMoreIcon
                                sx={serviceLineTreeFilterStyles.chevronIcon}
                              />
                            ) : (
                              <ChevronRightIcon
                                sx={serviceLineTreeFilterStyles.chevronIcon}
                              />
                            )}
                          </IconButton>
                        )}
                        <Typography
                          onClick={() => {
                            if (section.space_organization_unit_id !== null) {
                              handleSelectValue(
                                String(section.space_organization_unit_id),
                              );
                            }
                          }}
                          sx={{
                            ...serviceLineTreeFilterStyles.sectionHeaderTypography,
                            ...serviceLineTreeFilterStyles.sectionHeaderTitle,
                            ...(hasSectionChildren
                              ? {}
                              : {
                                  pl: `${serviceLineTreeFilterLayout.chevronColWidthPx}px`,
                                }),
                            ...(showSectionHeaderPointer
                              ? { cursor: "pointer" }
                              : {}),
                          }}
                        >
                          {section.name}
                        </Typography>
                      </Box>

                      <Collapse
                        in={sectionExpanded}
                        timeout="auto"
                        unmountOnExit
                      >
                        <TreeRows
                          nodes={sectionChildren}
                          depth={0}
                          expanded={nodeExpanded}
                          toggle={toggleNode}
                          onSelectValue={handleSelectValue}
                          selectedServiceLine={serviceLine}
                          treeKeyPrefix={`${index}-`}
                        />
                      </Collapse>
                    </React.Fragment>
                  );
                })}
              </Box>
            )}
          </Box>
        </Popover>
      ) : null}
    </>
  );
};
